import { create } from '@bufbuild/protobuf'
import { anyUnpack } from '@bufbuild/protobuf/wkt'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import { useGrpcClients } from '@/contexts/GrpcClientContext'
import {
    UserConfigurationEntryDto,
    UserConfigurationEntryDto_BooleanSettingSchema,
    UserConfigurationEntryDto_IdentifierDto,
    UserConfigurationEntryDto_IdentifierDtoSchema,
} from '@/generated/Messages/Basic_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import { parseId } from '@/helper/id'
import { configurationUserQueryKeyPrefix } from '@/helper/queryKeyPrefixes'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

export const useUserSettings = <T>(
    query: OmitDeepMessageFields<UserConfigurationEntryDto_IdentifierDto>,
    select: (data: UserConfigurationEntryDto | null) => T
) => {
    const clients = useGrpcClients()

    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useSuspenseQuery({
        queryKey: [configurationUserQueryKeyPrefix, query],
        queryFn: async ({ signal }) => {
            assertNotNullish(clients.configuration)

            try {
                return await clients.configuration.getUserConfigurationEntry(
                    query,
                    { signal }
                )
            } catch {
                return null
            }
        },
        select,
    })
}

export const useBooleanUserSetting = (configurationKey: string) => {
    const { user } = useAuth()

    return useUserSettings(
        create(UserConfigurationEntryDto_IdentifierDtoSchema, {
            configurationKey,
            userId: user?.profile.sub ? parseId(user.profile.sub) : undefined,
        }),
        data =>
            data?.configurationValue ?
                (anyUnpack(
                    data.configurationValue,
                    UserConfigurationEntryDto_BooleanSettingSchema
                )?.value ?? false)
            :   false
    )
}
